import { useMemo } from "react";
import { Box, Group, Text } from "@mantine/core";
import { IconRosetteDiscountCheckFilled } from "@tabler/icons-react";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import TableRowLinkOverlay from "@/components/shared/TableRowLinkOverlay";
import TableRowLinkTableStyles from "@/components/shared/TableRowLinkTableStyles";
import { getCollaborationRoleLabel, getIndustryLabel } from "@/components/shared/constants/general.const";
import InterestRequestActions from "@/pages/interests/components/InterestRequestActions";
import { buildCompanyDiscoverPath, companyProfileAriaLabel } from "@/shared/utils/companyProfileLink.util";

type CompanyInfo = {
  id: string;
  gtid: string;
  legalName?: string;
  headquartersCountry?: string;
  industryCategory?: string[];
  status?: string;
};

export type InterestRequestTableRow = {
  matchId: string;
  company: CompanyInfo;
  collaborationStatus: string;
  isRequester: boolean;
  requesterPaid: boolean;
  receiverPaid: boolean;
  requesterSignedNDA: boolean;
  receiverSignedNDA: boolean;
  paymentDeadline?: Date | string | null;
  requesterRole: string | null;
  expectedPartnerRole: string | null;
};

/** Matches `MatchedPrfofielTab` express-interest role labels. */
function collaborationRoleText(role: string | null | undefined) {
  if (!role?.trim()) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Text size="sm" c="slate.7" fw={500}>
      {getCollaborationRoleLabel(role)}
    </Text>
  );
}

function toTableRows(requests: any[], isRequester: boolean): InterestRequestTableRow[] {
  return (requests ?? []).map((request) => {
    const requesterCompany = request.requester as CompanyInfo | undefined;
    const receiverCompany = request.receiver as CompanyInfo | undefined;
    const company: CompanyInfo | undefined = isRequester ? receiverCompany : requesterCompany;
    if (!company?.id) return null;
    return {
      matchId: request.id,
      company,
      collaborationStatus: request.status,
      isRequester,
      requesterPaid: request.requesterPaid,
      receiverPaid: request.receiverPaid,
      requesterSignedNDA: request.requesterSignedNDA,
      receiverSignedNDA: request.receiverSignedNDA,
      paymentDeadline: request.paymentDeadline,
      requesterRole: request.requesterRole ?? null,
      expectedPartnerRole: request.expectedPartnerRole ?? null,
    };
  }).filter(Boolean) as InterestRequestTableRow[];
}

interface InterestRequestsTableProps {
  requests: any[];
  isRequester: boolean;
  isLoading: boolean;
  actionLoading: string | null;
  onAccept: (matchId: string) => void;
  onDecline: (matchId: string) => void;
  onMakePayment: (matchId: string) => void;
  onViewProfile: (companyId: string) => void;
  onOpenWorkspace: (matchId: string) => void;
}

export default function InterestRequestsTable({
  requests,
  isRequester,
  isLoading,
  actionLoading,
  onAccept,
  onDecline,
  onMakePayment,
  onViewProfile,
  onOpenWorkspace,
}: InterestRequestsTableProps) {
  const data = useMemo(() => toTableRows(requests, isRequester), [requests, isRequester]);

  const columns = useMemo<MRT_ColumnDef<InterestRequestTableRow>[]>(
    () => [
      {
        id: "company",
        header: "Company",
        size: 200,
        Cell: ({ row }) => {
          const { company } = row.original;
          return (
            <>
              <TableRowLinkOverlay
                to={buildCompanyDiscoverPath(company.id)}
                ariaLabel={companyProfileAriaLabel(company.gtid)}
              />
              <Group gap={6} wrap="nowrap">
                <Text size="sm" fw={500} c="primary.7" style={{ cursor: "pointer" }}>
                  {company.gtid || "N/A"}
                </Text>
                {company.status === "VERIFIED" && (
                  <IconRosetteDiscountCheckFilled
                    size={16}
                    style={{ color: "var(--mantine-color-primary-6)", flexShrink: 0 }}
                  />
                )}
              </Group>
            </>
          );
        },
      },
      {
        id: "industry",
        header: "Industry",
        size: 180,
        Cell: ({ row }) => {
          const cats = row.original.company.industryCategory;
          if (!Array.isArray(cats) || cats.length === 0) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }
          return (
            <Text size="sm">
              {getIndustryLabel(cats[0])}
              {cats.length > 1 ? ` +${cats.length - 1}` : ""}
            </Text>
          );
        },
      },
      {
        id: "requesterCollaborationRole",
        header: isRequester ? "Your role in this collaboration" : "Their role in this collaboration",
        size: 200,
        Cell: ({ row }) => collaborationRoleText(row.original.requesterRole),
      },
      {
        id: "expectedPartnerRole",
        header: isRequester ? "Expected Partner Role" : "What they expect from you",
        size: 200,
        Cell: ({ row }) => collaborationRoleText(row.original.expectedPartnerRole),
      },
      {
        id: "location",
        header: "Location",
        size: 120,
        Cell: ({ row }) => {
          const loc = row.original.company.headquartersCountry;
          return (
            <Text size="sm" c={loc ? undefined : "dimmed"}>
              {loc || "—"}
            </Text>
          );
        },
      },
      {
        id: "actions",
        header: "",
        size: 280,
        enableSorting: false,
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <Box data-surface-action>
              <InterestRequestActions
                matchId={r.matchId}
                companyId={r.company.id}
                collaborationStatus={r.collaborationStatus}
                isRequester={r.isRequester}
                requesterPaid={r.requesterPaid}
                receiverPaid={r.receiverPaid}
                requesterSignedNDA={r.requesterSignedNDA}
                receiverSignedNDA={r.receiverSignedNDA}
                paymentDeadline={r.paymentDeadline}
                onAccept={onAccept}
                onDecline={onDecline}
                onMakePayment={onMakePayment}
                onViewProfile={onViewProfile}
                onOpenWorkspace={onOpenWorkspace}
                isLoading={actionLoading === r.matchId}
                variant="table"
              />
            </Box>
          );
        },
      },
    ],
    [actionLoading, isRequester, onAccept, onDecline, onMakePayment, onOpenWorkspace, onViewProfile],
  );

  return (
    <Box className="gt-table-row-link">
      <TableRowLinkTableStyles />
      <MantineReactTable
        columns={columns}
        data={data}
        state={{ isLoading }}
        enablePagination={false}
        enableBottomToolbar={false}
        enableTopToolbar={false}
        enableColumnActions={false}
        enableSorting={false}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        mantineTableContainerProps={{ style: { minHeight: 280 } }}
        mantineTableBodyRowProps={() => ({
          "data-row-link": "",
          style: { cursor: "pointer", position: "relative" },
        })}
        mantineTableBodyCellProps={({ column }) => ({
          ...(column.id === "company" ? { "data-row-link-host": "", style: { position: "relative", overflow: "visible" } } : {}),
          ...(column.id === "actions" ? { "data-action-cell": "" } : {}),
        })}
      />
    </Box>
  );
}
